import React, { useState, useEffect, useContext } from "react";
import { Row, Col, Button, Modal } from "antd";
import { useIntl } from "react-intl";
import { useParams, useHistory } from "react-router-dom";
import { CreditCard, PaymentForm } from "react-square-web-payments-sdk";
import { useCreateSubscription, useGetContactCards } from "queries/memberships";
import { useGetLoyaltyContact } from "queries/loyaltyUsers";
import { useGetMerchant } from "queries/merchants";
import { EXISTING, NEW } from "constants/MembershipConstants";
import { getLocalStorageItem } from "utils/localStorage";
import ExistingCards from "views/app-views/SingleContactPage/MembershipsTab/creditCards";
import messages from "./translations";
import Loader from "shared/LoaderComponent";
import { ROUTES } from "../LoyaltyRoutes";
import { SuccessCheckIcon } from "assets/svg/membershipicons";
import { LoyaltyLayoutContext } from "layouts/app-loyalty-layout/context";

const CheckoutPage = () => {
  const params = useParams();
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { data: loyaltyContact } = useGetLoyaltyContact();
  const contactId = loyaltyContact?.data?.id;
  const organizationId = getLocalStorageItem("selectedOrganization");

  const { pathTitle } = useContext(LoyaltyLayoutContext);

  const [showAddNewCard, setShowAddNewCard] = useState(EXISTING);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showModal, setShowModal] = useState({ success: false, error: false });

  const subscribe = useCreateSubscription(
    formatMessage(messages.successfullSubscription),
    () => setShowModal({ ...showModal, success: true })
  );

  const { data: response, isLoading: contactCardsLoading } = useGetContactCards(
    { contact_id: contactId },
    !!contactId
  );

  const { data: merchant } = useGetMerchant({
    organization_id: organizationId,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mobileToken = params.get("mobile_token");
    const organization = params.get("organization");
    const redirectUri = params.get("redirect_uri");

    if (mobileToken) {
      try {
        const token = JSON.parse(decodeURIComponent(mobileToken));
        localStorage.setItem("token", JSON.stringify(token));
      } catch (e) {
        console.error("Failed to parse mobile token", e);
      }
    }
    if (organization) {
      localStorage.setItem("selectedOrganization", organization);
    }
    if (redirectUri) {
      localStorage.setItem("mobileRedirectUri", redirectUri);
    }
  }, []);

  useEffect(() => {
    if (response?.data?.length) {
      setShowAddNewCard(EXISTING);
    } else {
      setShowAddNewCard(NEW);
    }
  }, [response?.data]);

  const handleSubmit = (token, verifiedBuyer) => {
    const cardPayload = {
      brand: token?.details?.card?.brand,
      expire_month: token?.details?.card?.expMonth,
      expire_year: token?.details?.card?.expYear,
      last_digits: token?.details?.card?.last4,
      billing_name: `${loyaltyContact?.data?.name ?? ""} ${
        loyaltyContact?.data?.last_name ?? ""
      }`.trim(),

      token: token?.token,
    };

    if (token?.details?.billing?.postalCode) {
      cardPayload.postal_code = token?.details?.billing?.postalCode;
    }

    const payload = {
      card: cardPayload,
      organization: organizationId,
      membership_variation: params?.variation_id,
      contact: contactId,
    };
    subscribe.mutate(payload);
  };

  const handleUseCard = () => {
    subscribe.mutate({
      card: selectedCard?.provider_id,
      membership_variation: params?.variation_id,
      contact: contactId,
      organization: organizationId,
    });
  };

  const handleCloseModal = () => {
    setShowModal({ ...showModal, success: false });

    const redirectUri = localStorage.getItem("mobileRedirectUri");
    if (redirectUri) {
      localStorage.removeItem("mobileRedirectUri");
      window.location.href = "aeva-patients://membership-success";
    } else {
      history.push(ROUTES.PROFILE_MEMBERSHIPS);
    }
  };

  return (
    <div className="loyalty-membership-purchase_container">
      {showAddNewCard == EXISTING && (
        <>
          <Row>
            <Col span={24}>
              <p className="m-0 loyalty-membership_add_card">
                {formatMessage(messages.selectCard)}
              </p>
            </Col>
          </Row>
          <Row>
            <div
              className="w-100 mb-4 gray-border-rounded p-2"
              style={{ maxHeight: 240, overflowY: "auto" }}
            >
              <ExistingCards
                justifyCenter={true}
                cards={response?.data}
                setSelectedCard={setSelectedCard}
                selectedCard={selectedCard}
                handleNextStep={() => {}}
              />
            </div>
          </Row>

          <Row>
            <Col span={24}>
              <Button
                className="w-100 mb-2 loyalty-pay-btn"
                onClick={() => handleUseCard()}
                disabled={!selectedCard}
              >
                {formatMessage(messages.payMembership)}
              </Button>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Button
                className="w-100 loyalty-add-new-card-btn"
                onClick={() => setShowAddNewCard(NEW)}
              >
                {formatMessage(messages.addNewCard)}
              </Button>
            </Col>
          </Row>
        </>
      )}
      <Loader isLoading={contactCardsLoading || subscribe.isLoading} />
      {showAddNewCard == NEW && (
        <Row>
          <Col span={24}>
            <p className="m-0 loyalty-membership_add_card">
              {formatMessage(messages.addNewCardText)}
            </p>
          </Col>
        </Row>
      )}

      {showAddNewCard == NEW && merchant?.data?.location_id && (
        <>
          <Row className="mt-2">
            <Col span={24}>
              <PaymentForm
                applicationId={process.env.REACT_APP_SQUARE_CLIENT_ID}
                locationId={merchant?.data?.location_id}
                cardTokenizeResponseReceived={handleSubmit}
              >
                <CreditCard
                  buttonProps={{
                    css: {
                      backgroundColor: "#2F6873",
                      borderRadius: "6px",
                      fontSize: "14px",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#2F6873",
                      },
                      width: "100%",
                    },
                  }}
                ></CreditCard>
              </PaymentForm>
            </Col>
          </Row>
          {response?.data?.length > 0 && (
            <Row className="mt-2">
              <Col span={24}>
                <Button
                  className="w-100 loyalty-add-new-card-btn"
                  onClick={() => setShowAddNewCard(EXISTING)}
                >
                  {formatMessage(messages.useExistingCard)}
                </Button>
              </Col>
            </Row>
          )}
        </>
      )}
      <Modal
        centered
        footer={null}
        closable={false}
        visible={showModal.success}
      >
        <div className="d-flex flex-column align-items-center justify-content-center">
          <SuccessCheckIcon />
          <p className="heading-2 text-center">
            {formatMessage(messages.payMembershipSuccess)}
          </p>
          <p className="body-input text-center w-75">
            {formatMessage(messages.payMembershipDetails, {
              name: pathTitle,
            })}
          </p>
          <Button
            onClick={handleCloseModal}
            className="see-all-memberships-btn"
          >
            {formatMessage(messages.seeMyMembership)}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CheckoutPage;
